import { Cancel01Icon, Search01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import {
  Colors,
  Modal,
  TextInput,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useCallback, useRef, useState } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { TextInput as PaperTextInput, Portal } from 'react-native-paper';
import { PersonSearchDropdown } from './PersonSearchDropdown';

export function PersonSearchWithDropdownResults() {
  const [search, setSearch] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const inputRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const { isMobile } = useMediaQueries();
  const { height } = useWindowDimensions();
  const styles = useDynamicStyles(createStyles, { screenHeight: height });

  const showDropdown = search.length > 0 && isPositioned;

  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.measureInWindow((x, y, width, height) => {
        setMenuPosition({
          top: y + height + 4,
          left: x,
          width: width,
        });
        setIsPositioned(true);
      });
    }
  }, []);

  const handleFocus = useCallback(() => {
    if (isMobile) {
      setIsDropdownVisible(true);
    } else {
      // Show results again if there's search text
      if (search.length > 0) {
        updateDropdownPosition();
      }
    }
  }, [isMobile, search.length, updateDropdownPosition]);

  const hideResults = useCallback(() => {
    // Hide results but keep search text
    setIsPositioned(false);
  }, []);

  const closeDropdown = useCallback(() => {
    setSearch('');
    setIsDropdownVisible(false);
    setIsPositioned(false);
  }, []);

  const handleClear = useCallback(() => {
    setSearch('');
    setIsPositioned(false);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      // Only measure position once when starting to type
      if (!isPositioned && value.length > 0) {
        updateDropdownPosition();
      }
    },
    [isPositioned, updateDropdownPosition],
  );

  const handleMobilePress = useCallback(() => {
    setIsDropdownVisible(true);
  }, []);

  // Mobile: full-screen modal
  if (isMobile) {
    return (
      <>
        <TouchableOpacity
          onPress={handleMobilePress}
          style={styles.inputContainer}
        >
          <View pointerEvents="none">
            <TextInput
              label="Search"
              showLabel={false}
              value=""
              onChange={() => {}}
              style={styles.searchInput}
              editable={false}
              dense
              left={
                <PaperTextInput.Icon
                  icon={() => (
                    <HugeiconsIcon icon={Search01Icon} color={Colors.text} />
                  )}
                />
              }
            />
          </View>
        </TouchableOpacity>

        <Modal visible={isDropdownVisible} hide={closeDropdown}>
          <PersonSearchDropdown onClose={closeDropdown} autoFocus />
        </Modal>
      </>
    );
  }

  // Desktop: dropdown below input
  return (
    <View ref={inputRef} style={styles.inputContainer}>
      <TextInput
        label="Search for someone..."
        showLabel={false}
        value={search}
        onChange={handleSearchChange}
        onFocus={handleFocus}
        style={styles.searchInput}
        autoCorrect={false}
        dense
        left={
          <PaperTextInput.Icon
            icon={() => (
              <HugeiconsIcon icon={Search01Icon} color={Colors.text} />
            )}
          />
        }
        right={
          search.length > 0 ? (
            <PaperTextInput.Icon
              icon={() => (
                <HugeiconsIcon icon={Cancel01Icon} color={Colors.text} />
              )}
              onPress={handleClear}
            />
          ) : undefined
        }
      />

      {showDropdown && (
        <Portal>
          <TouchableOpacity
            style={[styles.backdrop, { top: menuPosition.top }]}
            onPress={hideResults}
          >
            <View
              style={[
                styles.dropdownContainer,
                {
                  top: 0,
                  left: menuPosition.left,
                  width: menuPosition.width,
                },
              ]}
              onStartShouldSetResponder={() => true}
            >
              <View style={styles.dropdown}>
                <PersonSearchDropdown
                  onClose={closeDropdown}
                  search={search}
                  onSearchChange={setSearch}
                  showInput={false}
                />
              </View>
            </View>
          </TouchableOpacity>
        </Portal>
      )}
    </View>
  );
}

const createStyles = ({
  isMobile,
  screenHeight,
}: WithResponsive<{ screenHeight: number }>) =>
  StyleSheet.create({
    inputContainer: {
      width: isMobile ? '100%' : 400,
    },
    backdrop: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
      zIndex: 1000,
    },
    dropdownContainer: {
      position: 'absolute',
      backgroundColor: 'transparent',
      shadowColor: Colors.black,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
      borderRadius: 12,
      maxHeight: screenHeight ? screenHeight * 0.6 : 500,
      zIndex: 1001,
    },
    dropdown: {
      backgroundColor: Colors.purple0,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: Colors.opacity20,
      overflow: 'hidden',
      padding: 12,
      maxHeight: screenHeight ? screenHeight * 0.6 : 500,
    },
    searchInput: {
      backgroundColor: Colors.opacity05,
      borderRadius: 24,
      height: isMobile ? undefined : 40,
    },
    list: {
      maxHeight: 400,
    },
    listContent: {
      paddingBottom: 8,
    },
    listItem: {
      display: 'flex',
      flexDirection: 'row',
      gap: 20,
      alignItems: 'center',
      marginBottom: 12,
      paddingHorizontal: isMobile ? 4 : 8,
    },
  });
